/**
 * useStatusMessage - React Hook für Status-Nachrichten
 *
 * Integriert den StatusMessageService in React-Komponenten.
 * Unterstützt automatische Updates und Flash-Messages.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { statusMessageService } from './StatusMessageService';

/**
 * Hook für Status-Nachrichten
 * @param {Object} options - Optionen
 * @param {number} options.flashDuration - Dauer für Flash-Messages (default: 2000ms)
 * @returns {Object} - { message, messageType, updateMessage, flashMessage, getReaction }
 */
/**
 * Bestimmt die aktuelle Phase basierend auf GameContext
 * Verwendet für einheitliche Phasen-Erkennung
 *
 * @param {Object} gc - GameContext Objekt
 * @returns {string} - Phase-Name
 */
export function determineCurrentPhase(gc) {
  // Seal Distribution hat Priorität (ist Sub-Phase von exchange)
  if (gc.sealDistribution?.active) {
    return 'sealDistribution';
  }

  // Andere Exchange Sub-Phasen
  if (gc.exchangeState?.phase) {
    const subPhase = gc.exchangeState.phase;
    if (subPhase === 'cardCount') return 'cardCount';
    if (subPhase === 'giving' || subPhase === 'receiving') return 'exchanging';
  }

  // Hauptphasen
  if (gc.phase === 'shop') return 'shop';
  if (gc.phase === 'roundEnd') return 'roundEnd';
  if (gc.phase === 'victory') return 'gameEnd';

  return 'playing';
}

export function useStatusMessage(options = {}) {
  const { flashDuration = 2000 } = options;

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [situation, setSituation] = useState(null);

  const flashTimeoutRef = useRef(null);
  const lastContextRef = useRef(null);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Aktualisiert die Message basierend auf Kontext
   * @param {Object} context - Spielkontext
   */
  const updateMessage = useCallback((context) => {
    // Speichere letzten Kontext für Flash-Recovery
    lastContextRef.current = context;

    const result = statusMessageService.getMessage(context);
    setMessage(result.text);
    setSituation(result.situation);

    // MessageType basierend auf Priorität
    if (result.priority >= 100) {
      setMessageType('error');
    } else if (result.priority >= 70) {
      setMessageType('effect');
    } else if (result.priority >= 60) {
      setMessageType('action');
    } else {
      setMessageType('info');
    }
  }, []);

  /**
   * Zeigt eine temporäre Message an (höhere Priorität)
   * @param {string} text - Message-Text
   * @param {string} type - Message-Typ (info, action, effect, error)
   * @param {number} duration - Anzeigedauer in ms (optional)
   */
  const flashMessage = useCallback((text, type = 'action', duration = flashDuration) => {
    // Vorherigen Timer canceln
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
    }

    setMessage(text);
    setMessageType(type);

    // Nach Ablauf zurück zur letzten Kontext-Message
    flashTimeoutRef.current = setTimeout(() => {
      if (lastContextRef.current) {
        updateMessage(lastContextRef.current);
      }
    }, duration);
  }, [flashDuration, updateMessage]);

  /**
   * Setzt eine Message direkt (ohne Flash/Timeout)
   * Für Selection Feedback - bleibt bis zur nächsten Änderung
   * @param {string} text - Message-Text
   * @param {string} type - Message-Typ
   */
  const setDirectMessage = useCallback((text, type = 'action') => {
    // Vorherigen Flash-Timer canceln
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    setMessage(text);
    setMessageType(type);
  }, []);

  /**
   * Gibt eine Reaktions-Message zurück (ohne State-Update)
   * @param {string} reactionType - Typ der Reaktion
   * @param {Object} data - Platzhalter-Daten
   * @returns {string} - Reaktions-Message
   */
  const getReaction = useCallback((reactionType, data = {}) => {
    return statusMessageService.getReaction(reactionType, data);
  }, []);

  /**
   * Zeigt eine Reaktion als Flash-Message
   * @param {string} reactionType - Typ der Reaktion
   * @param {Object} data - Platzhalter-Daten
   * @param {number} duration - Anzeigedauer in ms (optional)
   */
  const flashReaction = useCallback((reactionType, data = {}, duration = flashDuration) => {
    const text = statusMessageService.getReaction(reactionType, data);
    if (text) {
      flashMessage(text, 'action', duration);
    }
  }, [flashDuration, flashMessage]);

  return {
    message,
    messageType,
    situation,
    updateMessage,
    flashMessage,
    flashReaction,
    getReaction,
    setDirectMessage,
  };
}

/**
 * Baut Kontext-Objekt aus GameState
 * Helper-Funktion für einfache Integration
 *
 * @param {Object} gameState - Globaler Spielzustand
 * @param {Object} options - Zusätzliche Optionen
 * @returns {Object} - Kontext für StatusMessageService
 */
export function buildMessageContext(gameState, options = {}) {
  const {
    phase,
    currentPlayerIndex,
    myPlayerIndex,
    players,
    lastPlayedCards,
    lastPlayType,
    drawPileCount,
    roundNumber,
    isFirstTurn,
    rankings,
    // Zusätzliche Options
    lastAction,
    wasInvalidPlay,
    jokerTriggered,
    sealTriggered,
    shopAction,
    shopActionSuccess,
    exchangeSubPhase,
    isCardCountSelector,
  } = { ...gameState, ...options };

  const isMyTurn = currentPlayerIndex === myPlayerIndex;
  const myPlayer = players?.[myPlayerIndex];
  const activePlayer = players?.[currentPlayerIndex];
  const nextPlayerIndex = (currentPlayerIndex + 1) % (players?.length || 1);
  const nextPlayer = players?.[nextPlayerIndex];
  const amINext = nextPlayerIndex === myPlayerIndex;

  // Prüfe ob ein Gegner nur noch 1 Karte hat
  let opponentAlmostDone = false;
  let opponentWarningName = '';
  if (players) {
    for (let i = 0; i < players.length; i++) {
      if (i !== myPlayerIndex && players[i].hand?.length === 1) {
        opponentAlmostDone = true;
        opponentWarningName = players[i].name;
        break;
      }
    }
  }

  // hasValidPlays wird von außen übergeben (aus useCardSelection)
  // Fallback: wenn nicht übergeben, prüfe nur ob Hand Karten hat
  const hasValidPlays = options.hasValidPlays ?? myPlayer?.hand?.length > 0;
  const canDraw = options.canDraw ?? drawPileCount > 0;

  // Meine Rolle im Exchange (options.myRole hat Vorrang, da korrekt berechnet in useExchangePhase)
  let myRole = options.myRole || null;
  if (!myRole && rankings && myPlayerIndex !== undefined) {
    const myRankPosition = rankings.indexOf(myPlayerIndex);
    if (myRankPosition === 0) myRole = 'president';
    else if (myRankPosition === 1 && players?.length >= 4) myRole = 'vicePresident';
    else if (myRankPosition === rankings.length - 1) myRole = 'loser';
    else if (myRankPosition === rankings.length - 2 && players?.length >= 4) myRole = 'viceLoser';
    else myRole = 'citizen';
  }

  return {
    phase,
    isMyTurn,
    isFirstTurn: isFirstTurn || false,
    isRoundStarter: isMyTurn && (!lastPlayedCards || lastPlayedCards.length === 0),
    hasValidPlays,
    canDraw,
    myHandCount: myPlayer?.hand?.length || 0,
    shieldUses: myPlayer?.shieldUses || 0,
    activePlayerName: activePlayer?.name || 'Spieler',
    nextPlayerName: nextPlayer?.name || 'Jemand',
    amINext,
    roundNumber: roundNumber || 1,
    myPosition: myPlayer?.finishPosition,
    totalPlayers: players?.length || 2,
    opponentAlmostDone,
    opponentWarningName,
    lastAction,
    lastPlayedCards,
    lastPlayType,
    lastPlaySize: lastPlayedCards?.length || 0,
    wasInvalidPlay,
    jokerTriggered,
    sealTriggered,
    shopAction,
    shopActionSuccess,
    exchangeSubPhase,
    isCardCountSelector,
    myRole,
    isMyExchangeTurn: phase === 'exchanging' && isMyTurn,
    isMyShopTurn: phase === 'shop' && isMyTurn,
  };
}

export default useStatusMessage;
